import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Droplets, LogOut, Languages, Sun, Moon, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import HelpButton from "@/components/HelpButton";
import SiteFooter from "@/components/SiteFooter";
import CookieConsent from "@/components/CookieConsent";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t, primeFromUser } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();

  // When the user session becomes available, prime language from server preference once.
  React.useEffect(() => {
    if (user && user.language) primeFromUser(user.language);
  }, [user, primeFromUser]);

  const [dark, setDark] = React.useState(
    () => document.documentElement.classList.contains("dark"),
  );
  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Link
            to={user ? "/app" : "/"}
            className="flex items-center gap-2"
            data-testid="brand-link"
          >
            <div className="grid h-9 w-9 place-items-center rounded-md bg-primary text-white">
              <Droplets className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-outfit text-lg font-bold tracking-tight">
                Water
              </div>
              <div className="label-caps -mt-0.5">Planner</div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <HelpButton />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLang(lang === "de" ? "en" : "de")}
              className="gap-1.5"
              data-testid="lang-toggle-btn"
            >
              <Languages className="h-4 w-4" />
              <span className="font-mono-jb text-xs uppercase">{lang}</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDark((d) => !d)}
              data-testid="theme-toggle-btn"
              aria-label="theme"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {user?.is_super_admin && (
              <Link
                to="/admin"
                data-testid="super-admin-link"
                className="mr-1 inline-flex items-center gap-1 rounded-md border border-primary/60 bg-primary/10 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/20"
              >
                <Shield className="h-3.5 w-3.5" />
                Master
              </Link>
            )}
            {user ? (
              <>
                <div className="hidden text-right sm:block">
                  <div className="text-sm font-semibold leading-tight">{user.name}</div>
                  <div className="label-caps -mt-0.5">
                    {user.role === "municipality"
                      ? t("municipality")
                      : user.role === "utility"
                        ? t("utility")
                        : t("pool_owner")}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await logout();
                    nav("/");
                  }}
                  className="gap-1.5"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  {t("logout")}
                </Button>
              </>
            ) : (
              loc.pathname !== "/login" && (
                <Button
                  size="sm"
                  onClick={() => nav("/login")}
                  data-testid="header-login-btn"
                >
                  {t("login")}
                </Button>
              )
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        {children}
      </main>

      <SiteFooter />
      <CookieConsent />
    </div>
  );
}
